/**
 * The one Markdown renderer: item descriptions, mission goals, comments, proposals. Rendered as
 * React elements from lib/markdown.ts, never as HTML: raw HTML stays text, links keep only safe
 * schemes and open in a new tab without an opener, and images load only from attachments
 * (attachment:<id>), never from another site. A ```mermaid fence draws a diagram (Diagram.tsx). Type, spacing and radius come from the tokens:
 * body text at text-body (text-ui for size sm), headings one step up at most. One-line places
 * (rows, captions, banners) strip Markdown to plain text instead of rendering it.
 */
import { Check, ChevronDown, FileText, ImageOff, Info, Lightbulb, MessageSquareWarning, OctagonAlert, TriangleAlert, WrapText } from 'lucide-react';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { parseAttachmentRef } from '@orgus/protocol';
import { cn } from '../lib/cn.ts';
import { formatInt } from '../lib/format.ts';
import { isDiagramLang } from '../lib/diagram.ts';
import { healPartial, inlineText, numericColumns, parseMarkdown } from '../lib/markdown.ts';
import type { MdAlign, MdBlock, MdCalloutKind, MdInline } from '../lib/markdown.ts';
import { Button } from './Button.tsx';
import { CopyButton } from './CopyButton.tsx';
import { Diagram } from './Diagram.tsx';
import { IconButton } from './IconButton.tsx';

/** How an attachment referenced from Markdown is shown. */
export interface ResolvedAttachment {
  name: string;
  /** The URL that shows it (images: the file itself). */
  src: string;
  /** The URL that downloads it. */
  downloadUrl: string;
  isImage: boolean;
}

export interface MarkdownProps {
  source: string;
  /** Look up attachment:<id> references; unknown ones show as "not available". */
  resolveAttachment?: (id: string) => ResolvedAttachment | null;
  /** An attachment image was clicked (open it in a lightbox). */
  onOpenAttachment?: (id: string) => void;
  className?: string;
  /** Smaller type for comments and cards. */
  size?: 'sm' | 'md';
  /** Still being written: an unfinished tail is repaired and a caret follows the last word. */
  streaming?: boolean;
}

export function Markdown({ source, resolveAttachment, onOpenAttachment, className, size = 'md', streaming = false }: MarkdownProps) {
  const blocks = useMemo(() => parseMarkdown(streaming ? healPartial(source) : source), [source, streaming]);
  const ctx: Ctx = { resolve: resolveAttachment, open: onOpenAttachment };
  return (
    <div className={cn('min-w-0 break-words text-fg', size === 'sm' ? 'text-ui' : 'text-body', '[&>*+*]:mt-2', className)} data-markdown="">
      {blocks.map((b, i) => (
        <Block key={i} block={b} ctx={ctx} />
      ))}
      {streaming && <span aria-hidden className="ml-0.5 inline-block h-4 w-0.5 translate-y-0.5 rounded-pill bg-fg-muted" data-caret="" />}
    </div>
  );
}

interface Ctx {
  resolve?: (id: string) => ResolvedAttachment | null;
  open?: (id: string) => void;
}

const HEADING_CLASS: Record<number, string> = {
  1: 'text-lead font-semibold',
  2: 'text-body font-semibold',
  3: 'text-body font-medium',
  4: 'text-ui font-semibold',
  5: 'text-ui font-medium',
  6: 'text-ui font-medium text-fg-muted',
};

function Block({ block, ctx }: { block: MdBlock; ctx: Ctx }): ReactNode {
  switch (block.type) {
    case 'heading': {
      const Tag = `h${Math.min(6, block.level + 2)}` as 'h3';
      return <Tag className={HEADING_CLASS[block.level]}>{inlines(block.children, ctx)}</Tag>;
    }
    case 'paragraph':
      return <p>{inlines(block.children, ctx)}</p>;
    case 'code':
      return isDiagramLang(block.lang) ? <Diagram source={block.text} /> : <CodeBlock lang={block.lang} text={block.text} />;
    case 'table':
      return <TableBlock align={block.align} head={block.head} rows={block.rows} ctx={ctx} />;
    case 'callout': {
      const look = CALLOUT[block.kind];
      const Icon = look.icon;
      return (
        <div role="note" className={cn('flex flex-col gap-1 rounded-card border-l-2 bg-raised px-3 py-2', look.line)}>
          <span className={cn('flex items-center gap-1.5 text-caption font-medium', look.ink)}>
            <Icon className="size-3.5" aria-hidden />
            {look.label}
          </span>
          <div className="[&>*+*]:mt-2">
            {block.children.map((b, i) => (
              <Block key={i} block={b} ctx={ctx} />
            ))}
          </div>
        </div>
      );
    }
    case 'quote':
      return (
        <blockquote className="border-l-2 border-border-strong pl-3 text-fg-muted [&>*+*]:mt-2">
          {block.children.map((b, i) => (
            <Block key={i} block={b} ctx={ctx} />
          ))}
        </blockquote>
      );
    case 'hr':
      return <hr className="border-border-subtle" />;
    case 'list': {
      const task = block.items.some((it) => it.checked !== null);
      const Tag = block.ordered ? 'ol' : 'ul';
      return (
        <Tag start={block.ordered && block.start !== 1 ? block.start : undefined} className={cn('flex flex-col gap-1', task ? 'pl-1' : 'pl-6', !task && (block.ordered ? 'list-decimal' : 'list-disc'))}>
          {block.items.map((it, i) => (
            <li key={i} className={cn(it.checked !== null && 'flex list-none items-start gap-2')}>
              {it.checked !== null && (
                <span
                  role="img"
                  aria-label={it.checked ? 'Done' : 'Not done'}
                  className={cn('mt-1 flex size-3.5 shrink-0 items-center justify-center rounded-control border', it.checked ? 'border-transparent bg-(--control-mid) text-(--control-fg)' : 'border-border-strong')}
                >
                  {it.checked && <Check className="size-3" strokeWidth={3} />}
                </span>
              )}
              <div className="min-w-0 flex-1 [&>*+*]:mt-1">
                {it.children.map((b, j) => (
                  <Block key={j} block={b} ctx={ctx} />
                ))}
              </div>
            </li>
          ))}
        </Tag>
      );
    }
  }
}

const CALLOUT: Record<MdCalloutKind, { label: string; icon: typeof Info; line: string; ink: string }> = {
  note: { label: 'Note', icon: Info, line: 'border-border-strong', ink: 'text-fg-muted' },
  tip: { label: 'Tip', icon: Lightbulb, line: 'border-border-strong', ink: 'text-fg-muted' },
  important: { label: 'Important', icon: MessageSquareWarning, line: 'border-accent', ink: 'text-accent-text' },
  warning: { label: 'Warning', icon: TriangleAlert, line: 'border-status-attention', ink: 'text-status-attention-ink' },
  caution: { label: 'Caution', icon: OctagonAlert, line: 'border-status-danger', ink: 'text-status-danger-ink' },
};

/** Code longer than this folds to its first lines with a way to open the rest. */
const CODE_FOLD_LINES = 18;

/** A code block with its language, line count, wrap toggle, copy, and a fold for long code. */
function CodeBlock({ lang, text }: { lang: string; text: string }) {
  const [wrap, setWrap] = useState(false);
  const [open, setOpen] = useState(false);
  if (!text.trim()) return null;
  const lines = text.split('\n').length;
  const long = lines > CODE_FOLD_LINES;
  return (
    <div className="overflow-hidden rounded-card border border-border-subtle bg-bg" data-lang={lang || undefined}>
      <div className="flex h-control-sm items-center gap-2 border-b border-border-subtle pl-3 pr-1 text-caption text-fg-subtle">
        <span className="font-mono">{lang || 'text'}</span>
        {long && <span className="num">{formatInt(lines)} lines</span>}
        <span className="ml-auto flex items-center">
          <IconButton size="xs" label={wrap ? 'Stop wrapping lines' : 'Wrap lines'} icon={<WrapText />} pressed={wrap} onClick={() => setWrap((w) => !w)} />
          <CopyButton text={text} label={lang ? `Copy ${lang}` : 'Copy code'} />
        </span>
      </div>
      <pre className={cn('px-3 py-2 font-mono text-caption', wrap ? 'whitespace-pre-wrap break-words' : 'overflow-x-auto', long && !open && 'max-h-80 overflow-y-hidden')}>
        <code>{text}</code>
      </pre>
      {long && (
        <div className="border-t border-border-subtle p-1">
          <Button variant="ghost" size="sm" iconRight={<ChevronDown className={cn('transition-transform duration-(--duration-fast)', open && 'rotate-180')} />} onClick={() => setOpen((o) => !o)}>
            {open ? 'Show fewer lines' : `Show all ${formatInt(lines)} lines`}
          </Button>
        </div>
      )}
    </div>
  );
}

/** Tables longer than TABLE_FOLD_ROWS show their first TABLE_FIRST_ROWS rows and a way to open the rest. */
const TABLE_FOLD_ROWS = 12;
const TABLE_FIRST_ROWS = 8;
const ALIGN: Record<'left' | 'center' | 'right', string> = { left: 'text-left', center: 'text-center', right: 'text-right' };

/** A GFM table: numbers right-aligned in tabular figures, the first text column emphasised, a fold and a copy. */
function TableBlock({ align, head, rows, ctx }: { align: MdAlign[]; head: MdInline[][]; rows: MdInline[][][]; ctx: Ctx }) {
  const [open, setOpen] = useState(false);
  const plain = useMemo(() => rows.map((r) => r.map(inlineText)), [rows]);
  const numeric = useMemo(() => numericColumns(plain, head.length), [plain, head.length]);
  const firstText = numeric.findIndex((n) => !n);
  const shown = rows.length > TABLE_FOLD_ROWS && !open ? rows.slice(0, TABLE_FIRST_ROWS) : rows;
  const tsv = () => [head.map(inlineText), ...plain].map((r) => r.join('\t')).join('\n');
  const alignOf = (k: number) => align[k] ?? (numeric[k] ? 'right' : 'left');
  // A wide table scrolls inside its frame; a fade on the side with more says so.
  const scroller = useRef<HTMLDivElement>(null);
  const [more, setMore] = useState({ left: false, right: false });
  const measure = useCallback(() => {
    const el = scroller.current;
    if (!el) return;
    const left = el.scrollLeft > 1;
    const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
    setMore((m) => (m.left === left && m.right === right ? m : { left, right }));
  }, []);
  useEffect(() => {
    measure();
    const el = scroller.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [measure, shown.length]);
  return (
    <div className="group/table relative" data-table="">
      {more.left && <span aria-hidden className="pointer-events-none absolute inset-y-px left-px z-(--z-sticky) w-6 rounded-l-card bg-gradient-to-r from-bg to-transparent" data-fade="left" />}
      {more.right && <span aria-hidden className="pointer-events-none absolute inset-y-px right-px z-(--z-sticky) w-6 rounded-r-card bg-gradient-to-l from-bg to-transparent" data-fade="right" />}
      <div ref={scroller} onScroll={measure} tabIndex={more.left || more.right ? 0 : undefined} aria-label={more.left || more.right ? 'Table, scroll sideways for more' : undefined} className="overflow-x-auto rounded-card border border-border-subtle">
        <table className="w-full border-collapse text-ui">
          <thead className="bg-raised">
            <tr>
              {head.map((h, k) => (
                <th key={k} scope="col" className={cn('px-3 py-2 text-caption font-medium text-fg-muted', ALIGN[alignOf(k)])}>
                  {inlines(h, ctx)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {shown.map((r, i) => (
              <tr key={i} className="border-t border-border-subtle hover:bg-hover">
                {r.map((c, k) => (
                  <td key={k} className={cn('px-3 py-2 align-top', ALIGN[alignOf(k)], numeric[k] ? 'num whitespace-nowrap' : 'min-w-24', k === firstText && 'font-medium text-fg')}>
                    {inlines(c, ctx)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <span className="absolute right-1 top-1 opacity-0 transition-opacity duration-(--duration-fast) focus-within:opacity-100 group-hover/table:opacity-100">
        <CopyButton text={tsv} label="Copy table" variant="secondary" />
      </span>
      {rows.length > TABLE_FOLD_ROWS && (
        <Button variant="ghost" size="sm" className="mt-1" iconRight={<ChevronDown className={cn('transition-transform duration-(--duration-fast)', open && 'rotate-180')} />} onClick={() => setOpen((o) => !o)}>
          {open ? 'Show fewer rows' : `Show all ${formatInt(rows.length)} rows`}
        </Button>
      )}
    </div>
  );
}

function inlines(nodes: MdInline[], ctx: Ctx): ReactNode {
  return nodes.map((n, i) => <Fragment key={i}>{inline(n, ctx)}</Fragment>);
}

function inline(n: MdInline, ctx: Ctx): ReactNode {
  switch (n.type) {
    case 'text':
      return n.text;
    case 'br':
      return <br />;
    case 'code':
      return <code className="rounded-control bg-hover px-1 py-px font-mono text-caption">{n.text}</code>;
    case 'strong':
      return <strong className="font-semibold">{inlines(n.children, ctx)}</strong>;
    case 'em':
      return <em>{inlines(n.children, ctx)}</em>;
    case 'del':
      return <del className="text-fg-muted">{inlines(n.children, ctx)}</del>;
    case 'link': {
      const id = parseAttachmentRef(n.href);
      if (n.href.startsWith('attachment:')) {
        const a = id ? ctx.resolve?.(id) : null;
        if (!a) return <Missing label={n.children} ctx={ctx} />;
        return (
          <a href={a.downloadUrl} className="text-accent-text underline-offset-2 hover:underline" download={a.name}>
            {inlines(n.children, ctx)}
          </a>
        );
      }
      const external = /^(https?|mailto):/i.test(n.href);
      return (
        <a href={n.href} className="text-accent-text underline-offset-2 hover:underline" {...(external ? { target: '_blank', rel: 'noopener noreferrer nofollow' } : {})}>
          {inlines(n.children, ctx)}
        </a>
      );
    }
    case 'image': {
      const id = parseAttachmentRef(n.src);
      const a = id ? ctx.resolve?.(id) : null;
      if (!a || !id) {
        // Images from other sites are never loaded: they could track who read this.
        return (
          <span className="inline-flex items-center gap-1 rounded-control bg-hover px-1.5 text-caption text-fg-muted">
            <ImageOff className="size-3.5" aria-hidden />
            {n.alt || 'Image'}
            {id ? ' (not available)' : ' (outside images are not shown)'}
          </span>
        );
      }
      if (!a.isImage) {
        return (
          <a href={a.downloadUrl} download={a.name} className="inline-flex items-center gap-1 text-accent-text hover:underline">
            <FileText className="size-3.5" aria-hidden />
            {n.alt || a.name}
          </a>
        );
      }
      const img = <img src={a.src} alt={n.alt || a.name} loading="lazy" decoding="async" referrerPolicy="no-referrer" className="max-h-120 max-w-full rounded-card border border-border-subtle" />;
      return ctx.open ? (
        <button type="button" className="block cursor-zoom-in rounded-card focus-visible:outline-offset-2" onClick={() => ctx.open?.(id)} aria-label={`Open ${n.alt || a.name}`}>
          {img}
        </button>
      ) : (
        img
      );
    }
  }
}

function Missing({ label, ctx }: { label: MdInline[]; ctx: Ctx }) {
  return (
    <span className="inline-flex items-center gap-1 text-fg-muted">
      {inlines(label, ctx)}
      <span className="text-caption">(attachment not available)</span>
    </span>
  );
}
